/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 *
 * Override: Birthday coupons (BA25/BA50) shown as one block with one Remove; removes each coupon via API.
 */
import React, {useState, useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Flex,
    Button,
    Stack,
    Text,
    Heading,
    Divider,
    Accordion,
    Skeleton,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useStyleConfig,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PromoCode, usePromoCode} from '../../components/promo-code'
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemOptionItems from '../item-variant/item-option-items'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantPrice from '../item-variant/item-price'
import {
    useAccessToken,
    useCommerceApi,
    useProducts,
    usePromotions,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {getUrl} from '../../utils/cloudinary-utils'
import ContentAsset from '../content-asset'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {BA25_COUPON_CODE, BA50_COUPON_CODE, LOYALTYBIRTHDAYBONUS} from '../../constants'
import {
    isBirthdayCouponCode,
    hasBasketLineWithCouponCode,
    getCharitableDonationItem,
    getSubTotal
} from '../../utils/basket-utils'
import BNPLCartMessages from '../../../app/pages/cart/partials/bnpl-messages'
import {useGlobaleContext} from '../../../app/contexts/globale'
import CustomApi from '../../hooks/use-custom-api'
import {useQueryClient} from '@tanstack/react-query'
import {Link as RouteLink} from 'react-router-dom'
import GiftCertificateItemSummary from './partials/giftCertificateItemSummary'
import CartItemBadges from '../item-variant/item-badges'
import {paidWithGC} from '../../api/utils/gcHelpers'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useToast} from '../../hooks/use-toast'
import LoadingSpinner from '../loading-spinner'

const GRAY_BORDER = '1px solid var(--chakra-colors-gray-400)'

const CartItems = ({basket, setProducts}) => {
    const productIds =
        [
            ...(basket?.productItems?.map(({productId}) => productId).filter(Boolean) || []),
            ...(basket?.giftCertificateItems?.map(({c_productID}) => c_productID).filter(Boolean) ||
                [])
        ].join(',') || ''
    const {data: products} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                // Convert array into key/value object with key is the product id
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            updateProductsByItemId[productItem.itemId] = {...currentProduct, ...productItem}
        })
        setProducts(products)
        return updateProductsByItemId
    }, [basket, products])

    const CheckoutBagItem = useStyleConfig('CheckoutBagItem')

    const giftCertificateItems = () => {
        if (!basket?.giftCertificateItems?.length) return []

        return basket.giftCertificateItems
    }

    const productItemOptions = (product) => {
        const optionItems = product?.optionItems?.filter(({optionValueId}) => optionValueId !== '0')
        return optionItems
    }

    return (
        <Stack {...CheckoutBagItem.itemWrapper} className="bag-item-wrapper">
            <Stack {...CheckoutBagItem.headingWrapper} className="bag-heading-wrapper">
                <Text {...CheckoutBagItem.bagLabel} className="bag-label">
                    <FormattedMessage
                        defaultMessage="Bag"
                        id="order_summary.cart_items.label.bag"
                    />
                </Text>
                <Button
                    as={RouteLink}
                    to="/cart"
                    variant="link"
                    {...CheckoutBagItem.editBagLink}
                    className="edit-bag-link"
                >
                    <FormattedMessage
                        defaultMessage="Edit Bag"
                        id="order_summary.cart_items.link.edit_bag"
                    />
                </Button>
            </Stack>
            <Stack
                {...CheckoutBagItem.bagLineItemWrapper}
                sx={{
                    '&::-webkit-scrollbar': {
                        width: '4px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'gray.500',
                        borderRadius: '0',
                        width: '5px',
                        display: 'block'
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                        borderRadius: '0',
                        width: '5px',
                        display: 'block'
                    },
                    userSelect: 'none',
                    scrollBehavior: 'smooth'
                }}
                className="bag-line-item-wrapper"
            >
                {Object.values(productsByItemId || {}).map((product, idx) => {
                    if (product.c_fluidConfigureRecipeID && product.id !== 'colorbar') {
                        return null
                    }
                    const isColorBar = product.id === 'colorbar'
                    const variant = {
                        ...product,
                        ...(products && products[product.productId]),
                        price: product.price
                    }
                    return (
                        <ItemVariantProvider
                            key={`order-summary-item-${product.productId}-${product.itemId}`}
                            index={idx}
                            variant={variant}
                        >
                            <Flex
                                {...CheckoutBagItem.bagLineItem}
                                className="bag-line-item"
                                sx={{
                                    '.image-box': {
                                        width: '120px',
                                        height: isColorBar ? '120px !important' : '150px',
                                        minWidth: '120px',
                                        minHeight: isColorBar ? '120px !important' : '150px'
                                    },
                                    '.image-box img': {
                                        mixBlendMode: 'multiply',
                                        aspectRatio: isColorBar ? '1' : '4 / 5',
                                        width: '120px',
                                        height: isColorBar ? '120px !important' : '150px',
                                        minHeight: isColorBar ? '120px !important' : '150px'
                                    },
                                    '.product-name-link': {
                                        color: 'black.100',
                                        fontSize: '18px',
                                        fontWeight: '400',
                                        lineHeight: 'normal',
                                        letterSpacing: '0.09px'
                                    },
                                    '.product-quantity': {
                                        color: 'black.100',
                                        fontWeight: '300'
                                    },
                                    '.render-price-set .current-price': {
                                        fontWeight: '400'
                                    },
                                    '.engraving-image-wrapper': {
                                        width: '72px',
                                        height: '90px'
                                    },
                                    '.engraving-image-wrapper img': {
                                        width: '72px',
                                        height: '90px'
                                    },
                                    '[data-is-sale="true"].current-price': {
                                        color: 'purple.600 !important'
                                    }
                                }}
                            >
                                <CartItemVariantImage product={product} />
                                <Stack
                                    {...CheckoutBagItem.bagLineItemDetail}
                                    className="bag-line-item-detail"
                                >
                                    <CartItemVariantName />
                                    {product?.optionItems && (
                                        <CartItemOptionItems
                                            optionItems={productItemOptions(product)}
                                        />
                                    )}
                                    <CartItemVariantAttributes includeQuantity excludeBonusLabel />
                                    <CartItemBadges
                                        product={product}
                                        showInventoryMessage={false}
                                        showBopisInventoryMessage={false}
                                        showGlobaleInventoryMessage={false}
                                    />
                                    {(product?.c_hasEngraving || product?.id === 'colorbar') && (
                                        <Text
                                            {...CheckoutBagItem.notReturnableLabel}
                                            className="not-returnable-label"
                                        >
                                            <FormattedMessage
                                                defaultMessage="This item is custom made for you and cannot be returned."
                                                id="order_summary.cart_items.not.returnable.label"
                                            />
                                        </Text>
                                    )}
                                    <CartItemVariantPrice
                                        baseDirection="row"
                                        productItem={product}
                                        currency={basket?.currency}
                                    />
                                </Stack>
                            </Flex>
                        </ItemVariantProvider>
                    )
                })}
                <GiftCertificateItemSummary
                    giftCertificateItems={giftCertificateItems()}
                    basket={basket}
                    shipmentData={[]}
                />
            </Stack>
        </Stack>
    )
}

CartItems.propTypes = {
    basket: PropTypes.object,
    setProducts: PropTypes.any
}

const OrderSummary = ({
    showPromoCodeForm = false,
    showCartItems = false,
    setProducts,
    isEstimate = false,
    isCheckout = false,
    currencySymbol,
    showPriceSkeleton,
    fontSize = 'md',
    isMobileTopSummary = false,
    isOrderSummaryExpanded = false,
    birthdayPromoCard,
    onToggle
}) => {
    const intl = useIntl()
    const showToast = useToast()
    // ChangeUp config
    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}
    const [showOrceDownWarning, setShowOrceDownWarning] = useState(false)
    var changeUpProductID = null
    try {
        const jsonConfig = JSON.parse(configChangeup)
        if (jsonConfig) {
            changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
        }
    } catch (e) {
        console.error(e.message)
    }
    const customapi = new CustomApi()
    const api = useCommerceApi()
    const queryClient = useQueryClient()
    const {data: customer} = useCurrentCustomer()
    const {proxy} = api.shopperExperience.clientConfig
    const {data: basket} = useCurrentBasket()
    const {getTokenWhenReady} = useAccessToken()
    const {removePromoCode, ...promoCodeProps} = usePromoCode()
    const OrderSummaryStyles = useStyleConfig('OrderSummaryStyles')
    const headingStyle = isMobileTopSummary
        ? OrderSummaryStyles.headingTop
        : OrderSummaryStyles.heading
    const summaryLabelStyle = isMobileTopSummary
        ? OrderSummaryStyles.summaryLabelTop
        : OrderSummaryStyles.summaryLabel
    const summaryPriceLabelStyle = isMobileTopSummary
        ? OrderSummaryStyles.summaryPriceLabelTop
        : OrderSummaryStyles.summaryPriceLabel
    const summaryTotalLabelStyle = isMobileTopSummary
        ? OrderSummaryStyles.summaryTotalLabelTop
        : OrderSummaryStyles.summaryTotalLabel
    const summaryDetailsWrapperStyle = isMobileTopSummary
        ? OrderSummaryStyles.summaryDetailsWrapperTop
        : OrderSummaryStyles.summaryDetailsWrapper
    const hasSummaryTotalBorder = isCheckout || isMobileTopSummary
    const summaryTotalBorder = hasSummaryTotalBorder ? GRAY_BORDER : 'none'
    const summaryTotalPaddingTop = isCheckout ? '15px' : isMobileTopSummary ? '8px' : '0'
    const [isRemovingBirthday, setIsRemovingBirthday] = useState(false)
    const {countryISO, currencyCode} = useGlobaleContext()
    const updateBasket = useShopperBasketsMutation('updateBasket')
    // If onToggle is passed in, the parent controls expansion. Otherwise, Accordion owns it.
    const isControlled = typeof onToggle === 'function'
    const orderSummaryRef = useRef(null)

    useEffect(() => {
        if (!isMobileTopSummary) return

        const collapseExpandedSummary = (event) => {
            const summary = orderSummaryRef.current
            if (!summary?.getClientRects().length) return

            const expandedButton = summary.querySelector('.summary-button[aria-expanded="true"]')
            if (!expandedButton || summary.contains(event.target)) return

            expandedButton.click()
        }

        document.addEventListener('click', collapseExpandedSummary)
        return () => document.removeEventListener('click', collapseExpandedSummary)
    }, [isMobileTopSummary])

    useEffect(() => {
        if (localStorage.getItem('ORCEError') === 'true') {
            setShowOrceDownWarning(true)
        } else {
            setShowOrceDownWarning(false)
        }
    }, [customer])

    const couponItems = basket?.couponItems || []
    const birthdayCouponItems = couponItems.filter((item) => isBirthdayCouponCode(item.code))
    const loyaltyBonusItems = couponItems.filter((item) => item.code === LOYALTYBIRTHDAYBONUS)
    const birthdayPromoItemsToRemove = [...birthdayCouponItems, ...loyaltyBonusItems]
    const otherCouponItems = couponItems.filter(
        (item) => item.code !== LOYALTYBIRTHDAYBONUS && !isBirthdayCouponCode(item.code)
    )

    const otherCouponCodes = new Set((otherCouponItems || []).map((i) => i?.code).filter(Boolean))

    const promoIds = [
        ...new Set(
            (basket?.productItems || [])
                .flatMap((p) => p?.priceAdjustments || [])
                .filter(
                    (adj) =>
                        adj?.couponCode && adj?.promotionId && otherCouponCodes.has(adj.couponCode)
                )
                .map((adj) => adj.promotionId)
        )
    ]

    const {data: promotionsDetails} = usePromotions(
        {parameters: {ids: promoIds.join(',')}},
        {enabled: promoIds.length > 0}
    )

    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }
    const hasBirthdayPromo = birthdayCouponItems.length > 0 || loyaltyBonusItems.length > 0

    // Only show 25% / 50% lines when a line item actually has that discount (from priceAdjustments)
    const hasLineWithBA25 = hasBasketLineWithCouponCode(basket, BA25_COUPON_CODE)
    const hasLineWithBA50 = hasBasketLineWithCouponCode(basket, BA50_COUPON_CODE)

    const handleRemoveBirthdayPromos = async () => {
        if (!basket?.basketId || birthdayPromoItemsToRemove.length === 0) return
        setIsRemovingBirthday(true)
        window.bdayPromosUpdating = true
        try {
            const token = await getTokenWhenReady()
            const parameters = {
                c_coupons: birthdayPromoItemsToRemove.map((c) => c.code).join(',')
            }
            const response = await customapi.post(
                token,
                'remove-coupon-line-items',
                '/',
                parameters,
                proxy
            )
            if (response?.status) {
                queryClient.invalidateQueries({
                    predicate: (query) =>
                        query.queryKey.some(
                            (segment) => typeof segment === 'string' && segment.includes('/baskets')
                        )
                })

                const body = {
                    c_refreshTimeStamp: new Date()
                }
                await updateBasket.mutate({
                    parameters: {basketId: basket.basketId},
                    body: body
                })
                showToast({
                    title: intl.formatMessage({
                        id: 'promo.code.removed',
                        defaultMessage: 'Promo code removed!'
                    }),
                    status: 'success'
                })
            }
        } catch (e) {
            console.warn('Error occurred in removing coupon line items', e.message)
            setIsRemovingBirthday(false)
        } finally {
            window.bdayPromosUpdating = false
            setIsRemovingBirthday(false)
        }
    }

    const shippingItem = basket.shippingItems?.[0]
    const hasShippingPromos = shippingItem?.priceAdjustments?.length > 0
    const hasPromos = hasBirthdayPromo || otherCouponItems.length > 0
    const gcPaymentInstruments = basket?.paymentInstruments?.filter(
        (pi) => pi.paymentMethodId === 'GIFT_CERTIFICATE'
    )
    const charitableDonationItem = getCharitableDonationItem(basket, changeUpProductID)
    const subTotal = getSubTotal(basket, changeUpProductID)
    const AppliedPromos = () => {
        return (
            <Stack
                className="applied-promos-container"
                p={4}
                border={hasPromos ? 'none' : '1px solid'}
                borderColor="gray.100"
                borderRadius="base"
                paddingLeft={hasPromos ? '0' : '1rem'}
                paddingTop={hasPromos ? '0' : '1rem'}
                bg="white"
            >
                {isRemovingBirthday && (
                    <LoadingSpinner
                        className="applied-promos-loading-spinner"
                        position="fixed"
                        top="auto"
                    />
                )}
                <Stack className="applied-promos-list" spacing={3}>
                    {hasBirthdayPromo && (
                        <Box className="birthday-promo-box">
                            <Text
                                className="birthday-promo-title"
                                {...OrderSummaryStyles.appliedPromotionTitle}
                            >
                                <FormattedMessage
                                    defaultMessage="Happy Birthday!"
                                    id="order_summary.birthday_promo.title"
                                />
                            </Text>
                            {/* Message from content assets (same IDs as SFRA iscontentasset) */}
                            {hasLineWithBA50 && hasLineWithBA25 && (
                                <ContentAsset
                                    className="birthday-promo-content birthday-promo-content--25-50"
                                    contentAssetId="loyalty-25-50-percent-applied"
                                    {...OrderSummaryStyles.appliedPromotionContent}
                                />
                            )}
                            {hasLineWithBA50 && !hasLineWithBA25 && (
                                <ContentAsset
                                    className="birthday-promo-content birthday-promo-content--50"
                                    contentAssetId="loyalty-50-percent-applied"
                                    {...OrderSummaryStyles.appliedPromotionContent}
                                />
                            )}
                            {hasLineWithBA25 && !hasLineWithBA50 && (
                                <ContentAsset
                                    className="birthday-promo-content birthday-promo-content--25"
                                    contentAssetId="loyalty-25-percent-applied"
                                    {...OrderSummaryStyles.appliedPromotionContent}
                                />
                            )}
                            {!hasLineWithBA25 && !hasLineWithBA50 && (
                                <ContentAsset
                                    className="birthday-promo-content birthday-promo-content--qualifying-only"
                                    contentAssetId="loyalty-qualifying-only"
                                    {...OrderSummaryStyles.appliedPromotionContent}
                                />
                            )}
                            {!basket.orderNo && (
                                <Button
                                    className="birthday-promo-remove-button"
                                    variant="link"
                                    size="sm"
                                    onClick={handleRemoveBirthdayPromos}
                                    disabled={isRemovingBirthday}
                                    {...OrderSummaryStyles.appliedPromotionRemoveButton}
                                    marginTop="15px"
                                >
                                    <FormattedMessage
                                        defaultMessage="Remove"
                                        id="order_summary.action.remove_birthday_promo"
                                    />
                                </Button>
                            )}
                        </Box>
                    )}
                    {otherCouponItems.map((item) => {
                        const adjustment = basket?.productItems
                            ?.flatMap((p) => p?.priceAdjustments || [])
                            .find((adj) => adj?.couponCode === item.code)

                        const promo = promotionsDetails?.data?.find(
                            (p) => p.id === adjustment?.promotionId
                        )

                        return (
                            <Flex
                                key={item.couponItemId}
                                className="promo-code-applied-item"
                                alignItems="start"
                                flexDirection="column"
                                gap="15px"
                            >
                                <Text
                                    flex="1"
                                    {...OrderSummaryStyles.appliedPromotionTitle}
                                    className="promo-code-applied-title"
                                >
                                    {item.code}
                                </Text>
                                {promo?.name && (
                                    <Text
                                        flex="1"
                                        {...OrderSummaryStyles.appliedPromotionTitle}
                                        marginTop="-15px"
                                        className="promo-code-applied-name"
                                    >
                                        {promo?.name}
                                    </Text>
                                )}
                                {!basket.orderNo && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={async () =>
                                            await removePromoCode(item.couponItemId)
                                        }
                                        className="promo-code-remove-button"
                                        {...OrderSummaryStyles.appliedPromotionRemoveButton}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Remove"
                                            id="order_summary.action.remove_promo"
                                        />
                                    </Button>
                                )}
                            </Flex>
                        )
                    })}
                </Stack>
            </Stack>
        )
    }

    const getPriceText = (price) => {
        return showPriceSkeleton ? (
            <Skeleton height="16px" width="49px" />
        ) : typeof price === 'number' ? (
            intl
                .formatNumber(price, {
                    style: 'currency',
                    currency: currencyCode || basket?.currency
                })
                ?.replace(currencyCode || basket?.currency, currencySymbol) || ''
        ) : (
            ''
        )
    }

    const paidWithGiftCard = paidWithGC(basket)

    return (
        <Stack
            ref={orderSummaryRef}
            data-testid="sf-order-summary"
            spacing={5}
            className="order-summary-stack"
        >
            <Accordion
                allowToggle
                index={isControlled ? (isOrderSummaryExpanded ? 0 : -1) : undefined}
                defaultIndex={!isControlled && isOrderSummaryExpanded ? 0 : -1}
                onChange={
                    isControlled
                        ? (expandedIndex) => {
                              const nextIsOpen = Array.isArray(expandedIndex)
                                  ? expandedIndex.includes(0)
                                  : expandedIndex === 0

                              onToggle(nextIsOpen)
                          }
                        : undefined
                }
                className="summary-accordion"
            >
                <AccordionItem className="summary-item" border="none">
                    {({isExpanded}) => (
                        <>
                            <AccordionButton
                                as={Button}
                                className="summary-button"
                                justifyContent="space-between"
                                variant="link"
                                padding="0"
                                marginBottom={isMobileTopSummary ? '5px' : '0'}
                            >
                                <Flex
                                    alignItems="center"
                                    justifyContent={
                                        isMobileTopSummary ? 'flex-start' : 'space-between'
                                    }
                                    gap="15px"
                                    width={isMobileTopSummary ? 'auto' : 'full'}
                                >
                                    <Heading
                                        {...headingStyle}
                                        className="summary-heading"
                                        fontSize={{
                                            base: isMobileTopSummary ? '14px' : '30px',
                                            lg: '30px'
                                        }}
                                        fontFamily={{
                                            base: isMobileTopSummary ? 'body' : 'heading',
                                            lg: 'heading'
                                        }}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Order Summary"
                                            id="cart_skeleton.heading.order_summary"
                                        />
                                    </Heading>
                                    <Image
                                        alt=""
                                        aria-hidden="true"
                                        className="expand-accordion"
                                        width={isMobileTopSummary ? '10px' : '15px'}
                                        height="15px"
                                        color="black"
                                        transform={isExpanded ? 'rotate(180deg)' : undefined}
                                        verticalAlign="middle"
                                        src={getUrl('UIImages/images/down-arrow-new.svg')}
                                    />
                                </Flex>
                                {isMobileTopSummary &&
                                    (isExpanded ? (
                                        <Text
                                            {...OrderSummaryStyles.mobileCloseLabel}
                                            className="summary-close-label"
                                        >
                                            <FormattedMessage
                                                defaultMessage="(Tap to Close)"
                                                id="cart.order_summary.tap_to_close"
                                            />
                                        </Text>
                                    ) : (
                                        <Text
                                            {...OrderSummaryStyles.mobileTotalPrice}
                                            className="basket-total"
                                        >
                                            {getPriceText(
                                                basket?.orderTotal - paidWithGiftCard ||
                                                    basket?.productTotal - paidWithGiftCard
                                            )}
                                        </Text>
                                    ))}
                            </AccordionButton>
                            <AccordionPanel px={0} mb={4} className="accordion-panel">
                                <Stack
                                    spacing={4}
                                    gap={isMobileTopSummary ? '8px' : '10px'}
                                    align="flex-start"
                                    role="region"
                                    className="order-summary-heading"
                                    aria-labelledby="order-summary-heading"
                                    sx={OrderSummaryStyles.summaryContentWrapper}
                                >
                                    <Stack
                                        w="full"
                                        gap={isMobileTopSummary ? '3px' : '10px'}
                                        borderTop={GRAY_BORDER}
                                        sx={summaryDetailsWrapperStyle}
                                    >
                                        <ContentAsset contentAssetId="inclement-weather-asset" />
                                        <Flex
                                            justify="space-between"
                                            aria-live="polite"
                                            aria-atomic="true"
                                        >
                                            <Text
                                                {...summaryLabelStyle}
                                                id="order_summary_subtotal_label"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Subtotal"
                                                    id="order_summary.label.subtotal"
                                                />
                                            </Text>
                                            <Text
                                                {...summaryPriceLabelStyle}
                                                id="order_summary_subtotal_value"
                                                data-currency={basket?.currency}
                                            >
                                                {getPriceText(subTotal)}
                                            </Text>
                                        </Flex>

                                        {basket.orderPriceAdjustments?.map((adjustment) => (
                                            <Flex
                                                justify="space-between"
                                                key={adjustment.priceAdjustmentId}
                                                aria-live="polite"
                                                aria-atomic="true"
                                            >
                                                <Text
                                                    id="order_summary_price_adjustment_label"
                                                    fontSize={fontSize}
                                                >
                                                    {adjustment.itemText}
                                                </Text>
                                                <Text
                                                    id="order_summary_price_adjustment_value"
                                                    color="green.600"
                                                    fontSize={fontSize}
                                                >
                                                    {getPriceText(adjustment.price)}
                                                </Text>
                                            </Flex>
                                        ))}

                                        <Flex
                                            justify="space-between"
                                            aria-live="polite"
                                            aria-atomic="true"
                                        >
                                            <Text
                                                id="order_summary_tax_label"
                                                {...summaryLabelStyle}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Sales Tax"
                                                    id="order_summary.label.sales.tax"
                                                />
                                            </Text>
                                            {basket.taxTotal != null ? (
                                                <Text
                                                    id="order_summary_tax_value"
                                                    {...summaryPriceLabelStyle}
                                                >
                                                    {getPriceText(basket.taxTotal)}
                                                </Text>
                                            ) : (
                                                <Text
                                                    id="order_summary_tax_value"
                                                    {...summaryPriceLabelStyle}
                                                >
                                                    TBD
                                                </Text>
                                            )}
                                        </Flex>

                                        {(countryISO === 'US' || isCheckout) && (
                                            <Flex
                                                justify="space-between"
                                                aria-live="polite"
                                                aria-atomic="true"
                                                className="shipping-flex-wrapper"
                                            >
                                                <Flex alignItems="center" className="shipping-flex">
                                                    <Text
                                                        id="order_summary_shipping_label"
                                                        {...summaryLabelStyle}
                                                        className="shipping-label-promo"
                                                    >
                                                        <FormattedMessage
                                                            defaultMessage="Shipping"
                                                            id="order_summary.label.shipping"
                                                        />
                                                        {hasShippingPromos && (
                                                            <Text
                                                                id="order_summary_shipping_promo_label"
                                                                as="span"
                                                                {...summaryLabelStyle}
                                                            >
                                                                {' '}
                                                                (
                                                                <FormattedMessage
                                                                    defaultMessage="Promotion Applied"
                                                                    id="order_summary.label.promo_applied"
                                                                />
                                                                )
                                                            </Text>
                                                        )}
                                                    </Text>
                                                </Flex>

                                                {basket.shippingTotal === 0 ? (
                                                    <Text
                                                        id="order_summary_shipping_value"
                                                        as="span"
                                                        className="summary-price-label"
                                                        {...summaryPriceLabelStyle}
                                                    >
                                                        <FormattedMessage
                                                            defaultMessage="Free"
                                                            id="order_summary.label.free"
                                                        />
                                                    </Text>
                                                ) : (
                                                    <Text
                                                        id="order_summary_shipping_value"
                                                        {...summaryPriceLabelStyle}
                                                        className="shipping-total"
                                                    >
                                                        {getPriceText(basket.shippingTotal)}
                                                    </Text>
                                                )}
                                            </Flex>
                                        )}
                                        {charitableDonationItem && (
                                            <Flex
                                                justify="space-between"
                                                aria-live="polite"
                                                className="charitable-donation-flex"
                                                aria-atomic="true"
                                            >
                                                <Text
                                                    {...summaryLabelStyle}
                                                    className="charitable-donation"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Charitable Donation"
                                                        id="charitable.donation"
                                                    />
                                                </Text>
                                                <Text
                                                    {...summaryPriceLabelStyle}
                                                    className="charitable-donation-total"
                                                >
                                                    {getPriceText(
                                                        charitableDonationItem.price ||
                                                            charitableDonationItem.priceAfterItemDiscount ||
                                                            charitableDonationItem.c_gePriceAfterDiscount
                                                    )}
                                                </Text>
                                            </Flex>
                                        )}
                                        {gcPaymentInstruments?.map((gcPaymentInstrument) => (
                                            <Flex
                                                justify="space-between"
                                                aria-live="polite"
                                                aria-atomic="true"
                                                key={gcPaymentInstrument.paymentInstrumentId}
                                            >
                                                <Text
                                                    id="order_summary_giftcard_label"
                                                    {...summaryLabelStyle}
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Gift Card Applied"
                                                        id="order_summary.label.giftcard"
                                                    />
                                                </Text>
                                                <Text
                                                    id="order_summary_giftcard_value"
                                                    {...summaryPriceLabelStyle}
                                                    className="tbd"
                                                >
                                                    -{getPriceText(gcPaymentInstrument.amount)}
                                                </Text>
                                            </Flex>
                                        ))}
                                    </Stack>

                                    <Stack
                                        spacing={4}
                                        w="full"
                                        gap={isMobileTopSummary ? '8px' : '15px'}
                                        marginTop="-1px"
                                        borderTop={summaryTotalBorder}
                                        paddingTop={summaryTotalPaddingTop}
                                    >
                                        <Flex
                                            w="full"
                                            justify="space-between"
                                            aria-live="polite"
                                            aria-atomic="true"
                                            className="summary-total"
                                        >
                                            {isEstimate ? (
                                                <Text
                                                    id="order_summary_total_estimate_label"
                                                    {...summaryTotalLabelStyle}
                                                    className="estimated-total"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Estimated Total"
                                                        id="order_summary.label.estimated_total"
                                                    />
                                                </Text>
                                            ) : (
                                                <Text
                                                    id="order_summary_total_label"
                                                    {...summaryTotalLabelStyle}
                                                    className="label-total"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Total"
                                                        id="order_summary.label.total"
                                                    />
                                                </Text>
                                            )}
                                            <Text
                                                id="order_summary_total_value"
                                                {...summaryTotalLabelStyle}
                                                className="summary-total-label"
                                            >
                                                {getPriceText(
                                                    basket?.orderTotal
                                                        ? basket?.orderTotal - paidWithGiftCard
                                                        : basket?.productTotal - paidWithGiftCard
                                                )}
                                            </Text>
                                        </Flex>

                                        {!isMobileTopSummary &&
                                            !isCheckout &&
                                            countryISO === 'US' &&
                                            !basket?.giftCertificateItems?.length && (
                                                <BNPLCartMessages
                                                    basketTotal={basket?.orderTotal}
                                                />
                                            )}
                                        {showOrceDownWarning && customer?.isRegistered && (
                                            <Box
                                                className="orce-down-message-wrapper"
                                                {...OrderSummaryStyles.warningWrapper}
                                            >
                                                <Text
                                                    className="orce-down-warning"
                                                    {...OrderSummaryStyles.warningText}
                                                >
                                                    <FormattedMessage
                                                        id="orce.down"
                                                        defaultMessage="Due to a server issue your birthday discount is temporarily unavailable. Please check back in a few minutes to access your discount."
                                                    />
                                                </Text>
                                            </Box>
                                        )}
                                        {showPromoCodeForm ? (
                                            <Box w="full">
                                                <PromoCode
                                                    {...promoCodeProps}
                                                    hasPromotions={hasPromos}
                                                    hasBirthdayPromo={hasBirthdayPromo}
                                                    appliedPromos={<AppliedPromos />}
                                                    birthdayPromoCard={birthdayPromoCard}
                                                    isCheckout={isCheckout}
                                                />
                                            </Box>
                                        ) : (
                                            <Divider />
                                        )}
                                    </Stack>
                                </Stack>
                            </AccordionPanel>
                        </>
                    )}
                </AccordionItem>
            </Accordion>
            {showCartItems && <CartItems basket={basket} setProducts={setProducts} />}
        </Stack>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    showPromoCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    isCheckout: PropTypes.bool,
    showPriceSkeleton: PropTypes.bool,
    currencySymbol: PropTypes.string,
    fontSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    isMobileTopSummary: PropTypes.bool,
    isOrderSummaryExpanded: PropTypes.bool,
    onToggle: PropTypes.func,
    setProducts: PropTypes.any,
    birthdayPromoCard: PropTypes.node
}

export default OrderSummary
